import { computed, ref, type Ref } from 'vue';
import { useFormatter } from '@/composables';
import {
	useProjectRevenueForecastQuery,
	type RevenueForecastPeriod,
} from '@/generated/graphql';
import { useSettings } from '@/composables/settings/useSettings';
import { Setting } from '@/constants/settings';
import { SocketMessageType } from '@/constants/socketMessages';
import { useRefetchOnSocketMessage } from '@/lib/apollo/apolloTools';
import { startOfMonth } from 'date-fns';
import { dateToMysqlString } from '@/composables/settings/useFormatter/formatterUtils';
import { RevenueFieldType, type RevenueFieldTypeValue } from './constants';

export type PeriodWithAdditionalFields = RevenueForecastPeriod & {
	total: number;
	totalPercentage: number;
	revenueTemp: string;
	forecastedRevenueTemp: string;
};

export const useProjectRevenueForecast = (
	projectId: string,
	focusedPeriodId?: Ref<RevenueForecastPeriod['id'] | null>,
	focusedFieldType?: Ref<RevenueFieldTypeValue | null>
) => {
	const { formatNumber } = useFormatter();
	const { setting } = useSettings();

	const currentPeriod = dateToMysqlString(startOfMonth(new Date()));

	const { loading, result, onResult } = useRefetchOnSocketMessage(
		useProjectRevenueForecastQuery({
			revenueForecastInput: { projectId },
		}),
		{
			messages: [
				SocketMessageType.PROJECT_CHANGED,
				SocketMessageType.PROJECT_TIMEFRAME_CHANGED,
			],
			filter: (message) => String(message.data.project_id) === projectId,
		}
	);

	const revenueForecast = computed(
		() => result.value?.ProjectRevenueForecast
	);

	//forecast input currency will identified when there are more than one currency defined for the site
	//if project has pricelist, then this is the currency for forecast input. Otherwise base currency will be used
	const forecastCurrency = computed(() => {
		if ((result.value?.CurrenciesList || []).length > 1) {
			return (
				revenueForecast.value?.project?.priceList?.currency ??
				(setting(Setting.BASE_CURRENCY) || undefined)
			);
		}

		return undefined;
	});

	const quotedRevenue = computed(() => {
		return revenueForecast.value?.quotedTotal ?? 0;
	});

	const budgetType = computed(() => {
		return revenueForecast.value?.project.budgetType;
	});

	const usePassThroughCost = computed(() => {
		return !!revenueForecast.value?.project.useBillsExpensesInCalculations;
	});

	const totalEarned = computed(
		() =>
			revenueForecast.value?.periods.reduce(
				(sum, period) => sum + (period.earnedRevenue || 0),
				0
			) || 0
	);

	const totalRecognized = computed(
		() =>
			revenueForecast.value?.periods.reduce((sum, period) => {
				let total = period.revenue || 0;

				// If current month's values are set by user, consider only forecasted revenue if set
				if (
					period.isRevenueSetByUser &&
					period.period === currentPeriod &&
					period.forecastedRevenue !== null
				) {
					total = 0;
				}

				return sum + total;
			}, 0) || 0
	);

	const totalForecasted = computed(
		() =>
			revenueForecast.value?.periods.reduce(
				(sum, period) => sum + (period.forecastedRevenue || 0),
				0
			) || 0
	);

	const totalSum = computed(
		() => totalRecognized.value + totalForecasted.value
	);

	const forecastPeriods = ref<PeriodWithAdditionalFields[]>([]);

	/**
	 * Extracts value from the currently focused field before data refresh.
	 * This keeps user input during GraphQL refetches.
	 */
	function extractFocusedTempValue(
		periods: PeriodWithAdditionalFields[],
		focusedPeriodId: RevenueForecastPeriod['id'] | null,
		focusedFieldType: RevenueFieldTypeValue | null
	): string | null {
		if (!focusedPeriodId || !focusedFieldType) {
			return null;
		}

		const activePeriod = periods.find((p) => p.id === focusedPeriodId);
		if (!activePeriod) {
			return null;
		}

		return focusedFieldType === RevenueFieldType.RECOGNIZED
			? activePeriod.revenueTemp
			: activePeriod.forecastedRevenueTemp;
	}

	function transformPeriod(
		period: RevenueForecastPeriod,
		quotedRevenue: number,
		currentPeriod: string
	): PeriodWithAdditionalFields {
		let total = (period.revenue || 0) + (period.forecastedRevenue || 0);

		// If current month's values are set by user, consider only forecasted revenue if set
		if (
			period.isRevenueSetByUser &&
			period.period === currentPeriod &&
			period.forecastedRevenue !== null
		) {
			total = period.forecastedRevenue || 0;
		}

		return {
			total,
			totalPercentage:
				quotedRevenue > 0 ? (total / quotedRevenue) * 100 : 0,
			revenueTemp:
				typeof period.revenue === 'number' &&
				period.isRevenueSetByUser &&
				period.forecastedRevenue === null
					? String(period.revenue)
					: '',
			forecastedRevenueTemp:
				period.forecastedRevenue !== null && period.isRevenueSetByUser
					? String(period.forecastedRevenue)
					: '',
			...period,
		};
	}

	/**
	 * This ensures user input is maintained during GraphQL refetches
	 */
	function restoreFocusedTempValue(
		periods: PeriodWithAdditionalFields[],
		focusedPeriodId: RevenueForecastPeriod['id'] | null,
		focusedFieldType: RevenueFieldTypeValue | null,
		preservedValue: string | null
	): void {
		if (!focusedPeriodId || !focusedFieldType || preservedValue === null) {
			return;
		}

		const periodToRestore = periods.find((p) => p.id === focusedPeriodId);
		if (!periodToRestore) {
			return;
		}

		if (focusedFieldType === RevenueFieldType.RECOGNIZED) {
			periodToRestore.revenueTemp = preservedValue;
		} else {
			periodToRestore.forecastedRevenueTemp = preservedValue;
		}
	}

	onResult((result) => {
		const preserved = extractFocusedTempValue(
			forecastPeriods.value,
			focusedPeriodId?.value ?? null,
			focusedFieldType?.value ?? null
		);

		forecastPeriods.value = result.data.ProjectRevenueForecast.periods.map(
			(period) =>
				transformPeriod(period, quotedRevenue.value, currentPeriod)
		);

		restoreFocusedTempValue(
			forecastPeriods.value,
			focusedPeriodId?.value ?? null,
			focusedFieldType?.value ?? null,
			preserved
		);
	});

	const formatAmount = (amount: number) =>
		formatNumber(amount, {
			decimalPrecision: 2,
			forceDecimalForInteger: false,
		});

	return {
		loading,
		revenueForecast,
		forecastCurrency,
		quotedRevenue,
		forecastPeriods,
		totalEarned,
		totalRecognized,
		totalForecasted,
		totalSum,
		formatAmount,
		budgetType,
		usePassThroughCost,
	};
};
